// AI passport-import (Bescherming tile) — the "AI uitlezen" call. Upload one or more passport photos,
// the configured AI provider extracts vaccination lines (matched onto the shelter's product catalog),
// and the review dialog lets the operator pick/correct them before creating the rows through the normal
// POST /api/animals/{id}/vaccinations endpoint. Mirrors the Jmix VaccinationPassportAiService flow.
// Multipart + a bearer-only token (in memory, not a cookie), so these go through fetch, not the generic
// Refine dataProvider (same reason as animalPhoto.ts).
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** An HTTP failure carrying the status code, so callers can branch on 400 (bad upload). */
export class PassportApiError extends Error {
  readonly statusCode: number;
  constructor(statusCode: number, message?: string) {
    super(message ?? `HTTP ${statusCode}`);
    this.statusCode = statusCode;
    this.name = "PassportApiError";
  }
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function base(animalId: string): string {
  return `${API_URL}/animals/${animalId}/vaccinations`;
}

/** One extracted vaccination line. `productId` is set when a catalog product matched (null otherwise). */
export interface PassportSuggestion {
  productId: number | null;
  productName: string | null;
  type: string | null;
  brand: string | null;
  administrationDate: string | null;
  validFrom: string | null;
  validTo: string | null;
  /** Geen enting maar een titerbepaling (VacciCheck en soortgelijke): een meting van antistoffen. */
  titerbepaling: boolean;
  /**
   * Of deze regel al bij het dier staat. De server vergelijkt dat nu al tijdens het uitlezen, zodat
   * een tweede foto van hetzelfde boekje niet stilletjes niets doet. Leeg als er niets te
   * vergelijken viel.
   */
  status: "NIEUW" | "REEDS_AANWEZIG" | "MOGELIJK_DUPLICAAT" | null;
  /** Waarom de server dat vindt; bedoeld om te tonen. */
  reden: string | null;
}

/** Eén uitslag van een titerbepaling; `voldoende` leeg = het paspoort zegt er niets over. */
export interface PassportTiterUitslag {
  ziekte: string;
  score: string | null;
  voldoende: boolean | null;
}

/** Eén titerbepaling uit het paspoort: een meting van antistoffen, met per ziekte een uitslag. */
export interface PassportTiterSuggestie {
  datum: string | null;
  merk: string | null;
  uitslagen: PassportTiterUitslag[];
  /** Deze meting staat al bij het dier; hij hoeft er niet nog een keer in. */
  alAanwezig: boolean;
}

export interface PassportScanResult {
  available: boolean;
  message: string;
  suggestions: PassportSuggestion[];
  /** Titerbepalingen zijn geen entingen; ze worden apart vastgelegd. */
  titerbepalingen: PassportTiterSuggestie[];
}

/** Scan the uploaded passport photos and return extracted vaccination suggestions. Throws on HTTP failure. */
export async function scanPassport(animalId: string, files: File[]): Promise<PassportScanResult> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  const res = await fetch(`${base(animalId)}/passport-scan`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new PassportApiError(res.status, res.statusText);
  return res.json() as Promise<PassportScanResult>;
}

/** One user-confirmed candidate row sent to the import endpoint (`beginDatum` = toedien- of vanaf-datum). */
export interface PassportImportRow {
  inentingProductId: number | null;
  naam: string;
  merk: string | null;
  beginDatum: string; // yyyy-MM-dd
  eindDatum: string | null;
}

/** Per-row import outcome (index matches the request order). */
export interface PassportImportPerRow {
  index: number;
  status: "NIEUW" | "REEDS_AANWEZIG" | "MOGELIJK_DUPLICAAT";
  reason: string | null;
}

/** The import summary: how many rows were added vs skipped (and why) + how many files were archived. */
export interface PassportImportResult {
  added: number;
  reedsAanwezig: number;
  mogelijkDuplicaat: number;
  archivedFiles: number;
  archiveErrors: string[];
  perRow: PassportImportPerRow[];
}

/**
 * Confirm the AI passport-import: persist only the NIEUW rows (server-side fuzzy dedup, tolerance 1 day)
 * and ALWAYS archive every uploaded file (images + PDFs) into Media & Documenten — even on 0 imports.
 * One multipart call replaces the old per-row POST loop + client-side dedup. Throws on HTTP failure.
 */
export async function importPassport(
  animalId: string,
  files: File[],
  rows: PassportImportRow[],
  refDate: string,
): Promise<PassportImportResult> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  form.append("rows", JSON.stringify({ refDate, rows }));
  const res = await fetch(`${base(animalId)}/passport-import`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new PassportApiError(res.status, res.statusText);
  return res.json() as Promise<PassportImportResult>;
}

/** Whether an AI provider is configured — gates the import action. Never throws (false on error). */
export async function fetchPassportAiAvailability(animalId: string): Promise<boolean> {
  try {
    const res = await fetch(`${base(animalId)}/passport-availability`, { headers: authHeaders() });
    if (!res.ok) return false;
    const body = (await res.json()) as { available?: boolean };
    return body.available === true;
  } catch {
    return false;
  }
}
